import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Fuel, UploadCloud, Loader2, AlertCircle, Trash2,
  RefreshCw, X, BarChart3, List, TrendingUp, Wallet,
  FileArchive, FileText, Droplets
} from "lucide-react";
import { toast } from "sonner";

const PERIOD_OPTIONS = [
  { id: "week", label: "Esta semana" },
  { id: "month", label: "Este mes" },
  { id: "3months", label: "Últimos 3 meses" },
  { id: "year", label: "Este año" },
  { id: "custom", label: "Personalizado" },
];

function getPeriodDates(period) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split("T")[0];
  if (period === "week") {
    const mon = new Date(today);
    mon.setDate(today.getDate() - today.getDay() + 1);
    return { from: fmt(mon), to: fmt(today) };
  }
  if (period === "month") return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) };
  if (period === "3months") {
    const d = new Date(today); d.setMonth(d.getMonth() - 3);
    return { from: fmt(d), to: fmt(today) };
  }
  if (period === "year") return { from: `${today.getFullYear()}-01-01`, to: fmt(today) };
  return { from: "", to: "" };
}

function StatCard({ icon: Icon, label, value, unit, color = "#007934", sub }) {
  return (
    <div className="card-white p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: color + "18" }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-slate-400 font-medium">{label}</p>
        <p className="text-xl font-bold text-slate-800 leading-tight">
          {value !== null && value !== undefined ? (typeof value === "number" ? value.toLocaleString("es-ES", { maximumFractionDigits: 2 }) : value) : "—"}
          {unit && <span className="text-sm font-normal text-slate-400 ml-1">{unit}</span>}
        </p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function MiniBarChart({ data, keyX, keyY, color = "#0891B2", formatY }) {
  if (!data || data.length === 0) return <p className="text-sm text-slate-400 text-center py-8">Sin datos</p>;
  const max = Math.max(...data.map(d => d[keyY]));
  return (
    <div className="flex items-end gap-2 h-28 w-full">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
          <div className="w-full rounded-t-sm transition-all cursor-default"
            style={{ height: `${max ? (d[keyY] / max) * 100 : 2}%`, backgroundColor: color, opacity: 0.8 }} />
          <div className="absolute bottom-full mb-1 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
            {d[keyX]}: {formatY ? formatY(d[keyY]) : d[keyY]}
          </div>
          <p className="text-[9px] text-slate-400 leading-none truncate w-full text-center">{d[keyX]?.slice(-5)}</p>
        </div>
      ))}
    </div>
  );
}

export default function FuelTickets() {
  const { authAxios } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [tickets, setTickets] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadMode, setUploadMode] = useState("pdf"); // "pdf" | "zip"
  const [workCenter, setWorkCenter] = useState("");
  const [workCenterOptions, setWorkCenterOptions] = useState([]);
  const [filterWorkCenter, setFilterWorkCenter] = useState("");
  const fileRef = useRef();
  const pollRef = useRef();

  useEffect(() => {
    authAxios.get("/contracts").then(r => setContracts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!contractId) { setWorkCenterOptions([]); return; }
    authAxios.get("/fuel-tickets/work-centers", { params: { contract_id: contractId } })
      .then(r => setWorkCenterOptions(r.data?.work_centers || []))
      .catch(() => {});
  }, [contractId]);

  useEffect(() => { loadData(); }, [contractId, period, customFrom, customTo, filterWorkCenter]);

  function getDates() {
    if (period === "custom") return { from: customFrom, to: customTo };
    return getPeriodDates(period);
  }

  async function loadData() {
    const { from, to } = getDates();
    if (!from || !to) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to });
      if (contractId) params.append("contract_id", contractId);
      if (filterWorkCenter) params.append("work_center", filterWorkCenter);
      const [tickRes, dashRes] = await Promise.all([
        authAxios.get(`/fuel-tickets?${params}`),
        authAxios.get(`/fuel-tickets/dashboard?${params}`),
      ]);
      setTickets(tickRes.data);
      setDashboard(dashRes.data);
    } catch {
      toast.error("Error cargando tickets de combustible");
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(fileList) {
    if (!fileList || fileList.length === 0) return;
    if (!contractId) { toast.error("Selecciona un contrato antes de subir"); return; }
    const file = fileList[0];
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "zip"].includes(ext)) { toast.error("Solo se admiten PDF o ZIP"); return; }

    setUploading(true);
    setBatchStatus(null);
    const fd = new FormData();
    fd.append("contract_id", contractId);
    fd.append("file", file);
    if (workCenter && workCenter.trim()) fd.append("work_center", workCenter.trim());
    try {
      const res = await authAxios.post("/fuel-tickets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBatchId(res.data.batch_id);
      const isZip = res.data.type === "zip";
      toast.success(`Procesando ${res.data.total_files} ticket(s)${isZip ? " desde ZIP" : ""} con IA...`);
      pollBatch(res.data.batch_id);
    } catch {
      toast.error("Error al subir el archivo");
      setUploading(false);
    }
  }

  function pollBatch(id) {
    pollRef.current = setInterval(async () => {
      try {
        const r = await authAxios.get(`/fuel-tickets/batch/${id}`);
        setBatchStatus(r.data);
        if (["completed", "partial", "failed"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          setUploading(false);
          if (r.data.status !== "failed") {
            toast.success(`Extracción completada: ${r.data.processed} ticket(s) importados`);
            loadData();
          } else {
            toast.error("Error en la extracción");
          }
        }
      } catch { clearInterval(pollRef.current); setUploading(false); }
    }, 3000);
  }

  useEffect(() => () => clearInterval(pollRef.current), []);

  function toggleSelect(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }
  function toggleAll() {
    setSelected(s => s.length === tickets.length ? [] : tickets.map(t => t.id));
  }

  async function handleDelete() {
    if (!selected.length) return;
    if (!window.confirm(`¿Eliminar ${selected.length} ticket(s) seleccionado(s)?`)) return;
    setDeleting(true);
    try {
      await authAxios.post("/fuel-tickets/bulk-delete", { ids: selected });
      setSelected([]);
      toast.success(`${selected.length} ticket(s) eliminado(s)`);
      loadData();
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-6 space-y-5 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <Fuel size={20} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Tickets de Combustible</h1>
            <p className="text-sm text-slate-400">Sube PDFs individuales o un ZIP con subcarpetas por matrícula</p>
          </div>
        </div>
        <button data-testid="refresh-fuel-btn" onClick={loadData}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 border border-slate-200 px-3 py-2 rounded-xl hover:bg-slate-50 transition-all">
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Upload Zone */}
      <div className="card-white p-5 space-y-4">
        {/* Mode selector */}
        <div className="flex items-center gap-3 mb-2">
          <p className="text-sm font-semibold text-slate-700">Formato de carga:</p>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            <button data-testid="upload-mode-pdf"
              onClick={() => setUploadMode("pdf")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${uploadMode === "pdf" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <FileText size={13} /> PDF individual
            </button>
            <button data-testid="upload-mode-zip"
              onClick={() => setUploadMode("zip")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${uploadMode === "zip" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <FileArchive size={13} /> ZIP por matrícula
            </button>
          </div>
        </div>

        {/* Upload instructions */}
        <div className={`rounded-xl px-4 py-3 text-xs ${uploadMode === "zip" ? "bg-blue-50 text-blue-700" : "bg-slate-50 text-slate-600"}`}>
          {uploadMode === "pdf"
            ? "Sube uno o varios tickets de combustible en formato PDF. La IA extraerá fecha, matrícula, litros, importe, estación y más."
            : "Sube un ZIP con subcarpetas nombradas con la matrícula del vehículo (ej: 5125BVY/enero.pdf). La matrícula se infiere automáticamente del nombre de la carpeta si no aparece en el ticket."}
        </div>

        {/* Centro / base de origen del lote (opcional, permite separar Meco / Los Santos, etc.) */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-semibold text-slate-500 uppercase">Centro/Base</label>
          <input
            data-testid="fuel-upload-work-center"
            list="fuel-wc-datalist"
            value={workCenter}
            onChange={e => setWorkCenter(e.target.value)}
            placeholder="Ej: Meco, Los Santos de la Humosa… (opcional)"
            className="flex-1 min-w-[220px] border border-slate-200 rounded-xl px-3 py-1.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          <datalist id="fuel-wc-datalist">
            {workCenterOptions.map((w, i) => <option key={i} value={w} />)}
          </datalist>
          <span className="text-[11px] text-slate-400">Se etiquetan todos los tickets del PDF/ZIP con este centro para separarlos después.</span>
        </div>

        <div
          data-testid="fuel-upload-zone"
          className={`border-2 border-dashed rounded-xl p-6 transition-all cursor-pointer ${dragOver ? "border-blue-500 bg-blue-50/30" : "border-slate-200 bg-slate-50/50 hover:border-blue-400/60"}`}
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        >
          <input ref={fileRef} type="file"
            accept={uploadMode === "zip" ? ".zip" : ".pdf"}
            className="hidden"
            onChange={e => handleUpload(e.target.files)} />
          <div className="flex flex-col items-center gap-2 text-center">
            {uploading
              ? <Loader2 size={28} className="text-blue-600 animate-spin" />
              : uploadMode === "zip" ? <FileArchive size={28} className="text-slate-400" /> : <UploadCloud size={28} className="text-slate-400" />}
            <p className="font-semibold text-slate-700 text-sm">
              {uploading ? "Procesando con Claude IA..." : uploadMode === "zip" ? "Arrastra el ZIP aquí" : "Arrastra el PDF aquí"}
            </p>
            <p className="text-xs text-slate-400">
              {uploadMode === "zip" ? "Estructura esperada: {MATRÍCULA}/{ticket}.pdf" : "Un ticket PDF de combustible"}
            </p>
            {!contractId && <p className="text-xs text-amber-500 font-medium">Selecciona un contrato antes de subir</p>}
          </div>
          {/* Batch Progress */}
          {batchStatus && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                <span>{batchStatus.processed} / {batchStatus.total} procesados</span>
                {batchStatus.errors > 0 && <span className="text-red-500">{batchStatus.errors} errores</span>}
                <span className={`font-semibold ${batchStatus.status === "completed" ? "text-green-600" : batchStatus.status === "failed" ? "text-red-600" : "text-blue-600"}`}>
                  {batchStatus.status === "completed" ? "Completado" : batchStatus.status === "failed" ? "Error" : batchStatus.status === "partial" ? "Parcial" : "Procesando..."}
                </span>
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-2 bg-blue-500 rounded-full transition-all"
                  style={{ width: `${batchStatus.total ? (batchStatus.processed / batchStatus.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card-white p-4 flex flex-wrap items-center gap-3 animate-fade-in-up">
        <select data-testid="fuel-contract-select"
          value={contractId} onChange={e => setContractId(e.target.value)}
          className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
          <option value="">Todos los contratos</option>
          {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {workCenterOptions.length > 0 && (
          <select
            data-testid="fuel-filter-work-center"
            value={filterWorkCenter}
            onChange={e => setFilterWorkCenter(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            title="Filtrar por centro/base"
          >
            <option value="">Todos los centros</option>
            {workCenterOptions.map((w, i) => <option key={i} value={w}>{w}</option>)}
          </select>
        )}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {PERIOD_OPTIONS.map(p => (
            <button key={p.id} data-testid={`fuel-period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${period === p.id ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm" />
            <span className="text-slate-400">→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm" />
          </div>
        )}
        <div className="ml-auto flex gap-1 bg-slate-100 p-1 rounded-xl">
          <button data-testid="fuel-view-list-btn" onClick={() => setView("list")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "list" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500"}`}>
            <List size={13} className="inline mr-1" />Lista
          </button>
          <button data-testid="fuel-view-dashboard-btn" onClick={() => setView("dashboard")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${view === "dashboard" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500"}`}>
            <BarChart3 size={13} className="inline mr-1" />Dashboard
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up">
          <StatCard icon={Fuel} label="Tickets importados" value={dashboard.total_tickets} color="#0891B2" />
          <StatCard icon={Droplets} label="Total litros" value={dashboard.total_liters} unit="L" color="#059669" />
          <StatCard icon={Wallet} label="Gasto total" value={dashboard.total_eur} unit="€" color="#B45309" />
          <StatCard icon={TrendingUp} label="Precio medio/litro" value={dashboard.avg_price_per_liter} unit="€/L" color="#7C3AED"
            sub={`${dashboard.by_matricula?.length || 0} vehículos`} />
        </div>
      )}

      {/* Delete bar */}
      {selected.length > 0 && (
        <div className="card-white p-3 flex items-center gap-3 border border-red-200 bg-red-50/50 animate-fade-in-up">
          <span className="text-sm font-semibold text-red-700">{selected.length} seleccionado(s)</span>
          <button data-testid="fuel-delete-selected-btn" onClick={handleDelete} disabled={deleting}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-60">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Eliminar selección
          </button>
          <button onClick={() => setSelected([])} className="text-slate-400 hover:text-slate-700 ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {/* List View */}
      {view === "list" && (
        <div className="card-white overflow-hidden animate-fade-in-up">
          {loading
            ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-500" /></div>
            : tickets.length === 0
            ? (
              <div className="text-center py-12">
                <Fuel size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-medium">No hay tickets en el periodo seleccionado</p>
                <p className="text-slate-300 text-sm mt-1">Sube un PDF o ZIP usando la zona de carga superior</p>
              </div>
            )
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="w-10 p-3">
                      <input type="checkbox" data-testid="fuel-select-all"
                        checked={selected.length === tickets.length && tickets.length > 0}
                        onChange={toggleAll} className="rounded accent-blue-500" />
                    </th>
                    {["Fecha", "Contrato", "Centro", "Matrícula", "Combustible", "Litros", "Importe", "€/L", "Estación"].map(h => (
                      <th key={h} className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t, i) => (
                    <tr key={t.id} data-testid={`fuel-row-${i}`}
                      className={`border-b border-slate-50 hover:bg-slate-50/70 transition-colors ${selected.includes(t.id) ? "bg-blue-50/40" : ""}`}>
                      <td className="p-3">
                        <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)}
                          className="rounded accent-blue-500" />
                      </td>
                      <td className="p-3 font-mono text-slate-600 text-xs">{t.date}</td>
                      <td className="p-3">
                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">
                          {contracts.find(c => c.id === t.contract_id)?.name || t.contract_id}
                        </span>
                      </td>
                      <td className="p-3">
                        {t.work_center ? (
                          <span
                            data-testid={`fuel-row-wc-${i}`}
                            className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-semibold"
                            title="Centro/base al que se asignó este ticket al subir"
                          >
                            {t.work_center}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-3 font-bold text-slate-800">{t.matricula || "—"}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.fuel_type?.toLowerCase().includes("diesel") ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"}`}>
                          {t.fuel_type || "—"}
                        </span>
                      </td>
                      <td className="p-3 font-semibold text-slate-800">
                        {t.liters != null ? t.liters.toLocaleString("es-ES", { maximumFractionDigits: 2 }) : "—"}
                      </td>
                      <td className="p-3 font-bold text-slate-800">
                        {t.amount_eur != null ? `${t.amount_eur.toLocaleString("es-ES", { maximumFractionDigits: 2 })} €` : "—"}
                      </td>
                      <td className="p-3 text-slate-500">
                        {t.price_per_liter != null ? `${t.price_per_liter.toFixed(3)} €` : "—"}
                      </td>
                      <td className="p-3 text-slate-400 text-xs max-w-[160px] truncate">{t.station_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {/* Dashboard View */}
      {view === "dashboard" && dashboard && (
        <div className="space-y-4 animate-fade-in-up">
          {/* Anomalies */}
          {dashboard.anomalies?.length > 0 && (
            <div className="card-white p-5 border-l-4 border-amber-400">
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-500" /> Anomalías detectadas
              </h3>
              <div className="space-y-2">
                {dashboard.anomalies.slice(0, 5).map((a, i) => (
                  <div key={i} className="text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 flex gap-3">
                    <span className="font-semibold text-amber-700">
                      {a.type === "consumo_alto" ? "Consumo inusualmente alto" : "Precio anómalo"}
                    </span>
                    <span className="text-amber-600">
                      {a.date} · {a.matricula} ·{" "}
                      {a.type === "consumo_alto" ? `${a.liters} L` : `${a.price} €/L`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By vehicle */}
            <div className="card-white p-5">
              <h3 className="font-bold text-slate-800 mb-4">Gasto por vehículo (€)</h3>
              {dashboard.by_matricula?.length > 0
                ? dashboard.by_matricula.slice(0, 8).map((v, i) => {
                    const max = dashboard.by_matricula[0]?.eur || 1;
                    return (
                      <div key={i} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-mono font-bold text-slate-700">{v.matricula}</span>
                          <span className="text-slate-400">
                            {v.eur.toLocaleString("es-ES", { maximumFractionDigits: 2 })} € · {v.liters.toFixed(1)} L · {v.count} repostajes
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full">
                          <div className="h-1.5 rounded-full" style={{ width: `${Math.round(v.eur / max * 100)}%`, backgroundColor: "#0891B2" }} />
                        </div>
                      </div>
                    );
                  })
                : <p className="text-sm text-slate-400">Sin datos</p>}
            </div>
            {/* Evolution by month */}
            <div className="card-white p-5">
              <h3 className="font-bold text-slate-800 mb-4">Gasto mensual (€)</h3>
              <MiniBarChart
                data={dashboard.by_month}
                keyX="month" keyY="eur"
                color="#0891B2"
                formatY={v => `${v.toFixed(2)} €`}
              />
            </div>
            {/* Liters by month */}
            <div className="card-white p-5 md:col-span-2">
              <h3 className="font-bold text-slate-800 mb-4">Litros consumidos por mes</h3>
              <MiniBarChart
                data={dashboard.by_month}
                keyX="month" keyY="liters"
                color="#059669"
                formatY={v => `${v.toFixed(1)} L`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
